// Resolução da URL do artefato de ENTREGA REAL de uma demanda (botão 👁 dos
// cards "aguardando aprovação"). Extraído de DashboardPage/DemandRichCard
// (cópias idênticas — unificação 2026-06-11) pra fonte única.
//
// DEM-2038: a versão antiga só resolvia URL http(s) literal em
// metadata.artifact_url / metadata.artifact / final_handoff.artifact_url /
// final_handoff.to_ref — então o botão ficava quase sempre DESABILITADO,
// mesmo quando havia entrega de verdade. Agora resolvemos o artefato de
// entrega REAL a partir das estruturas que o pipeline canonical produz:
//   • final_handoff / metadata `artifacts[]` (SkillArtifact.signed_url) —
//     entrega persistida em storage (PR/handoff de skill);
//   • final_handoff.to_ref / delivery_url / url quando for URL http(s);
//   • metadata.pr_url / pr.url / metadata.delivery.* — PR de engenharia;
//   • candidatos literais já cobertos antes (compat).
// Só retorna null — desabilitando o botão — quando comprovadamente NÃO há
// nenhum artefato resolvível.
import type { Demand } from '@/lib/api';
import { isSkillArtifactOrGroup } from '@/lib/types/skill-artifact';

const HTTP_RE = /^https?:\/\//i;

function asUrl(v: unknown): string | null {
  return typeof v === 'string' && HTTP_RE.test(v) ? v : null;
}

/** Primeiro signed_url de um array de SkillArtifact / media_group. */
function urlFromArtifacts(v: unknown): string | null {
  if (!Array.isArray(v)) return null;
  for (const item of v) {
    if (!isSkillArtifactOrGroup(item)) continue;
    if (item.type === 'media_group') {
      const inner = item.items.find((a) => asUrl(a.signed_url));
      if (inner) return inner.signed_url;
    } else {
      const u = asUrl(item.signed_url);
      if (u) return u;
    }
  }
  return null;
}

/** URL dentro de um objeto-handoff/delivery (to_ref, url, signed_url, pr_url). */
function urlFromContainer(v: unknown): string | null {
  if (typeof v !== 'object' || v === null) return null;
  const o = v as Record<string, unknown>;
  return (
    asUrl(o['artifact_url']) ??
    asUrl(o['signed_url']) ??
    asUrl(o['url']) ??
    asUrl(o['to_ref']) ??
    asUrl(o['delivery_url']) ??
    asUrl(o['pr_url']) ??
    urlFromArtifacts(o['artifacts'])
  );
}

/**
 * Resolve a URL do artefato de entrega real da demanda, ou null se não houver.
 * Ordem de preferência: artefatos persistidos (signed_url) → containers de
 * handoff/delivery/PR → candidatos literais legados.
 */
export function getArtifactUrl(d: Demand): string | null {
  const meta = (d.metadata ?? {}) as Record<string, unknown>;
  const handoff = (d.final_handoff ?? {}) as Record<string, unknown>;

  // 1. Artefatos persistidos (SkillArtifact[].signed_url) — entrega real.
  const fromArtifacts =
    urlFromArtifacts(handoff['artifacts']) ?? urlFromArtifacts(meta['artifacts']);
  if (fromArtifacts) return fromArtifacts;

  // 2. Containers de handoff / delivery / PR (objetos aninhados).
  const fromContainers =
    urlFromContainer(handoff) ??
    urlFromContainer(meta['delivery']) ??
    urlFromContainer(meta['pr']) ??
    urlFromContainer(meta);
  if (fromContainers) return fromContainers;

  // 3. Candidatos literais legados (compat — comportamento anterior).
  const candidates = [
    meta['artifact_url'],
    meta['artifact'],
    meta['pr_url'],
    handoff['artifact_url'],
    handoff['to_ref'],
  ];
  for (const c of candidates) {
    const u = asUrl(c);
    if (u) return u;
  }
  return null;
}
